import axios from "axios";
import { apiServiceConstants, CONSTANTS } from "../../constants";
import { AuthService } from "../../services/authService";
import { dataTransferService } from "../../services/dataTransferService";
import { pushNotificationService } from "../../services/PushNotification.service";
import { StorageService } from "../../services/storage.service";
import { utilService } from "../../services/utilService";
import { apiErrorNotifier } from "../../services/apiErrorNotifier";


///////////////////////////////////////////////////////////////
// For working in local admin server make it to TRUE else false.
const isLocalServerON : Boolean = false;
///////////////////////////////////////////////////////////////

const _getHeadersConfig = (isPostX = false) => {
  let config: any = {};

  const language = localStorage.getItem("appLanguage") || "en";
  config["Accept-Language"] = language;

  const savePassword = utilService.getSavePasswordStatus();

  const token = StorageService.getStorageItem(
    CONSTANTS.STORAGE[true === savePassword ? 1 : 0],
    "token",
    true
  );
  const isTokenValid = JSON.parse(
    StorageService.getStorageItem(localStorage, "isLoggedIn")
  );
  if (token && isTokenValid) {
    config["Authorization"] = `Token ${token}`;
    // The `signature` header that used to be set here was AES of a timestamp
    // under a key shipped in this bundle, and no service ever read it. See
    // signatureApiService.
  }
  if (!isPostX) {
    config["Accept"] = "application/json";
    config["Content-Type"] = "application/json";
  }

  return config;
};

const createAxiosInstance = () => {
  return axios.create({
    headers: _getHeadersConfig(),
  });
};

const getLocalServerUrl = (url: any) => {
  const baseappURL = (window as any)._env_?.REACT_APP_API_URL;
  const addOnURL = (window as any)._env_?.REACT_APP_API_ADD_ON_URL; // or create separate if needed

  let URL = `${baseappURL}${url}`;

  if (url.includes("sboard")) {
    URL = `${addOnURL}${url}`;
  }

  return URL;
};

const getServerUrl = (url: any) => {
  const baseappURL = (window as any)._env_?.REACT_APP_API_URL;
  const addOnURL = (window as any)._env_?.REACT_APP_API_ADD_ON_URL || (window as any)._env_?.REACT_APP_API_URL; // fallback

  let URL = `${baseappURL}${url}`;

  if (url.includes("sboard")) {
    URL = `${addOnURL}${url}`;
  }

  return URL;
};

const post = async (URL: any, params: any) => {
  const axiosInstance = createAxiosInstance();
  URL = isLocalServerON ? getLocalServerUrl(URL) : getServerUrl(URL);
  return axiosInstance({
    method: "POST",
    url: URL,
    data: params,
  })
    .then((response) => response.data)
    .catch((err) => handleApiStatus(err));
};

const head = async (URL: any, params: any) => {
  const axiosInstance = createAxiosInstance();
  URL = isLocalServerON ? getLocalServerUrl(URL) : getServerUrl(URL);
  return axiosInstance({
    method: "HEAD",
    url: URL,
    data: params,
  })
    .then((response) => response.data)
    .catch((err) => handleApiStatus(err));
};

const get = async (URL: any, params: any) => {
  const axiosInstance = createAxiosInstance();
  // URL = getUrl(URL);
  URL = isLocalServerON ? getLocalServerUrl(URL) : getServerUrl(URL);

  return axiosInstance({
    method: "GET",
    url: URL,
    params: params,
  })
    .then((response) => response.data)
    .catch((err) => handleApiStatus(err));
};

const put = async (URL: any, params: any) => {
  const axiosInstance = createAxiosInstance();
  URL = isLocalServerON ? getLocalServerUrl(URL) : getServerUrl(URL);
  return axiosInstance({
    method: "PUT",
    url: URL,
    data: params,
  })
    .then((response) => response.data)
    .catch((err) => handleApiStatus(err));
};

const remove = async (URL: any, params: any) => {
  const axiosInstance = createAxiosInstance();
  URL = isLocalServerON ? getLocalServerUrl(URL) : getServerUrl(URL);
  return axiosInstance({
    method: "DELETE",
    url: URL,
    data: params,
  })
    .then((response) => response.data)
    .catch((err) => handleApiStatus(err));
};


const processAPI = (url: String, params: any, method: String) => {

  let result = new Promise(() => {});  
  switch (method) {
    case apiServiceConstants.GET: {
      result = get(url, params);
      break;
    }
    case apiServiceConstants.POST: {
      result = post(url, params);
      break;
    }
    case apiServiceConstants.HEAD: {
      result = head(url, params);
      break;
    }
    case apiServiceConstants.PUT: {
      result = put(url, params);
      break;
    }
    case apiServiceConstants.DELETE: {
      result = remove(url, params);
      break;
    }
    default:
      break;
  }
  return result;
};

/**
 * Messages for the status codes the UI cannot say anything better about. The
 * server's own `message` is preferred whenever it sends one -- it is written
 * for this user, in their language, and these are not.
 */
const STATUS_MESSAGES: { [status: number]: string } = {
  400: "That request could not be processed. Please check the details and try again.",
  403: "You do not have permission to do that.",
  404: "We could not find what you asked for.",
  405: "That action is not available here.",
  406: "That request could not be processed.",
  408: "The server took too long to respond. Please try again.",
  413: "That file or message is too large to send.",
  422: "Some of the details are not valid. Please check them and try again.",
  423: "That item is locked and cannot be changed right now.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Something went wrong on our side. Please try again, or contact the site administrator if it persists.",
  501: "That feature is not available.",
  502: "The server is unreachable right now. Please try again shortly.",
  503: "The service is temporarily unavailable. Please try again shortly.",
  504: "The server took too long to respond. Please try again shortly.",
};

const OFFLINE_MESSAGE =
  "We could not reach the server. Check your connection and try again.";
const UNKNOWN_MESSAGE =
  "Something went wrong. Please try again, or contact the site administrator if the problem persists.";

const serverMessage = (err: any): string => {
  const data = err?.response?.data;
  if (typeof data === "string" && data.trim() && data.length < 200) {
    return data.trim();
  }
  const message = data?.message;
  return typeof message === "string" && message.trim() ? message.trim() : "";
};

/**
 * Turns a failed request into a value every caller can read.
 *
 * It used to switch on `err.response.status` with no guard, so a timeout, a
 * dropped connection or a blocked preflight -- where axios reports no
 * `response` at all -- threw `Cannot read properties of undefined` from inside
 * the error handler, losing the original failure. It also returned `err.data`,
 * a field axios never sets, so every caller received `undefined` and could not
 * tell a failure from an empty result; and it popped a blocking native
 * `alert()` for anything that was not a 401.
 *
 * Now it always returns the envelope the rest of the app expects
 * (`isSuccess: false` plus a message) and reports the failure through
 * apiErrorNotifier, which raises one toast.
 */
const handleApiStatus = (err: any) => {
  const status: number | undefined = err?.response?.status;

  if (401 === status) {
    dataTransferService.transferUnAuthorizedAction(true);
    logout();
    return { isSuccess: false, status, message: "401 Unauthorized", result: [] };
  }

  let message: string;
  if (status === undefined) {
    // No response: offline, DNS failure, timeout, or a preflight the browser
    // refused. There is no status to look up.
    message = OFFLINE_MESSAGE;
  } else {
    message = serverMessage(err) || STATUS_MESSAGES[status] || UNKNOWN_MESSAGE;
  }

  apiErrorNotifier.notifyApiError({ message, status });

  return { isSuccess: false, status, message, result: [] };
};

/**
 * A 401 means the token is already dead, so the revoke call this makes would
 * itself answer 401 and re-enter handleApiStatus. The flag breaks that loop:
 * the first logout wins and the rest are ignored.
 */
let isLoggingOut = false;

const logout = async () => {
  if (isLoggingOut) return;
  isLoggingOut = true;

  // Awaited rather than raced against a one-second timer: the revoke used to
  // be fired and abandoned, and AuthService.logout() navigates away, which
  // cancels an in-flight request.
  await pushNotificationService.deleteToken();
  AuthService.logout();
};

export const webApiService = {
  post,
  get,
  head,
  remove,
  processAPI,
};